import { Button } from "@acme/ui/button";
import { Card, CardContent, CardHeader } from "@acme/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@acme/ui/table";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Shield, Zap } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListViewCardsGrid } from "~/common/components/responsive-list-view.js";
import { ListViewToggle } from "~/common/components/list-view-toggle.js";
import { surfaceEmptyState, surfaceTableShell } from "~/common/components/surface-styles.js";
import { useListViewMode } from "~/common/hooks/use-list-view-mode.js";
import { api } from "~/common/utils/trpc.js";
import ClanIdentityLink from "~/common/components/clan-identity-link.js";
import { AddClanDialog } from "./_components/-add-clan-dialog.js";
import { ClanOverviewCard } from "./_components/-clan-overview-card.js";

export const Route = createFileRoute("/_authed/my-clans/")({
  component: RouteComponent,
});

function LoadingSkeletonTable() {
  return (
    <div className={surfaceTableShell}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Clan</TableHead>
            <TableHead>Level</TableHead>
            <TableHead>Mitglieder</TableHead>
            <TableHead>War Wins</TableHead>
            <TableHead>Meine Spieler</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 4 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <div className="h-5 w-36 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-8 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-12 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-12 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-10 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-20 rounded bg-muted animate-pulse" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function LoadingSkeletonCards() {
  return (
    <ListViewCardsGrid>
      {Array.from({ length: 6 }).map((_, i) => (
        <Card className="overflow-hidden" key={i}>
          <CardHeader className="pb-2">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-full bg-muted animate-pulse shrink-0" />
              <div className="flex-1 space-y-1">
                <div className="h-5 w-full rounded bg-muted animate-pulse" />
                <div className="h-3 w-20 rounded bg-muted animate-pulse" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2 pt-0">
            {Array.from({ length: 4 }).map((_, j) => (
              <div className="h-5 w-16 rounded-full bg-muted animate-pulse" key={j} />
            ))}
          </CardContent>
        </Card>
      ))}
    </ListViewCardsGrid>
  );
}

function RouteComponent() {
  const { t } = useTranslation();
  const { effectiveView, viewMode, setViewMode, isBelowLg } = useListViewMode();
  const clansQueryOptions = api.clan.getAllForOverviewByUserId.queryOptions();
  const { data: clans, isPending: isLoading } = useQuery(clansQueryOptions);
  const [addClanDialogOpen, setAddClanDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="h-9 w-36 rounded-md bg-muted animate-pulse" />
          <div className="flex gap-2">
            <ListViewToggle isBelowLg={isBelowLg} setViewMode={setViewMode} viewMode={viewMode} />
            <div className="h-9 w-28 rounded-md bg-muted animate-pulse" />
          </div>
        </div>
        {effectiveView === "table" ? <LoadingSkeletonTable /> : <LoadingSkeletonCards />}
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h1 className="text-3xl font-bold">{t("app.myClans.title")}</h1>
        <div className="flex items-center gap-2">
          <ListViewToggle isBelowLg={isBelowLg} setViewMode={setViewMode} viewMode={viewMode} />
          <Button onClick={() => setAddClanDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("app.myClans.addClan")}
          </Button>
        </div>
      </div>

      {clans == null || clans.length === 0 ? (
        <div className={`${surfaceEmptyState} py-16 text-center text-muted-foreground`}>
          <p className="text-lg font-medium mb-1">{t("app.myClans.emptyTitle")}</p>
          <p className="text-sm">{t("app.myClans.emptyDescription")}</p>
        </div>
      ) : effectiveView === "table" ? (
        <div className={surfaceTableShell}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("app.myClans.table.clan")}</TableHead>
                <TableHead>{t("app.myClans.table.level")}</TableHead>
                <TableHead>{t("app.myClans.table.members")}</TableHead>
                <TableHead>{t("app.myClans.table.warWins")}</TableHead>
                <TableHead>{t("app.myClans.table.myPlayers")}</TableHead>
                <TableHead>{t("app.myClans.table.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clans.map((clan) => (
                <TableRow key={clan.clanTag}>
                  <TableCell className="font-medium">
                    <ClanIdentityLink
                      badgeUrls={clan.clanBadgeUrls}
                      clanName={clan.clanName}
                      clanTag={clan.clanTag}
                    />
                  </TableCell>
                  <TableCell>{clan.clanLevel ?? "–"}</TableCell>
                  <TableCell>{clan.members ?? "–"}</TableCell>
                  <TableCell>{clan.warWins ?? "–"}</TableCell>
                  <TableCell>
                    <span>
                      {t("app.myClans.playersConnected", { count: clan.myPlayersConnected ?? 0 })}
                      {(clan.myPlayersRequested ?? 0) > 0 && (
                        <span className="text-muted-foreground">
                          {" · "}
                          {t("app.myClans.playersRequested", { count: clan.myPlayersRequested })}
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    {clan.hasActiveWar ? (
                      <span className="inline-flex items-center gap-1 text-destructive text-sm font-medium">
                        <Zap className="size-3.5" />
                        {t("app.myClans.warActive")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-muted-foreground text-sm">
                        <Shield className="size-3.5" />
                        {t("app.myClans.idle")}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <ListViewCardsGrid>
          {clans.map((clan) => (
            <ClanOverviewCard clan={clan} key={clan.clanTag} />
          ))}
        </ListViewCardsGrid>
      )}

      <AddClanDialog onOpenChange={setAddClanDialogOpen} open={addClanDialogOpen} />
    </div>
  );
}
